import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of, Subject } from 'rxjs';
import { catchError, concatMap, debounceTime, filter, map, mapTo, take, tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { Scheme } from './environment-diff.service';
import { QuotePermissionService } from './quote-permission.service';
import { PlanningType } from './projects.service';
import {
    ConfigitEngine,
    ConfigitModelQuoteLine,
    QuotationQuoteLine,
    QuoteAssignment,
    QuoteOffer,
    QuoteStatus,
} from '../models/quote.model';
import { ConfigitAceQuoteLineAdapter, ConfigitModelQuoteLineAdapter } from '../adapters/QuoteLineAdapter';
import { PrintType, PrintTypeLong, PrintTypeShort } from '../models/print-type';
import { ConfigitAceAssignment } from '../models/configit-ace.model';
import { getAceForConfigitModel, userAssignments } from '../utils/configit-ace.helper';

export interface QuoteDiscount {
    materialGroup: string;
    partnerDiscount: string; // this is in fact the quoteDiscount NOT the partner discount
}

@Injectable({
    providedIn: 'root',
})
export class QuoteService {
    private quote$: BehaviorSubject<Quote | undefined> = new BehaviorSubject(undefined);
    private modelLineAdapter: ConfigitModelQuoteLineAdapter;
    private aceLineAdapter: ConfigitAceQuoteLineAdapter;
    public quoteErrors$: Subject<any> = new Subject<any>();

    constructor(private httpService: HttpClient, private quoteValidation: QuotePermissionService) {
        this.quote$
            .pipe(
                filter(Boolean),
                filter((v: Quote) => !v?.isSaved),
                debounceTime(2500),
                concatMap((quote) =>
                    this.performQuoteUpdateRequest(quote).pipe(
                        map((v) => ({ ...v, isSaved: true })),
                        catchError((error) => {
                            this.quoteErrors$.next(error);
                            const quote = this.quote$.value;
                            const { version } = quote?.isLegacy ? quote?.configitQuote : quote?.quotationQuote;
                            return of({ version, isSaved: true });
                        })
                    )
                )
            )
            .subscribe(({ version, isSaved }) => {
                const data = this.quote$.getValue();
                if (data) {
                    const quote = data.isLegacy
                        ? { configitQuote: { ...data.configitQuote, version } }
                        : { quotationQuote: { ...data.quotationQuote, version } };
                    this.quote$.next({ ...data, ...quote, isSaved });
                }
            });
    }

    public getCurrentQuoteObservable() {
        return this.quote$.pipe(filter((q: Quote, index) => q === undefined || index === 0 || q.isNew || !q.isSaved));
    }

    public getCurrentQuote() {
        return this.quote$.value;
    }

    public fetchQuote(id: string, forceReload = false): Observable<Quote> {
        const quote = this.quote$.getValue();
        const currentId = quote?.id;
        if (!forceReload && currentId === id) {
            this.quote$.next({
                ...quote,
                isNew: true,
            });
            return of(this.quote$.value);
        }
        this.quote$.next(undefined);

        const isNumber = /^[+-]?\d+(\.\d+)?$/.test(id);
        return (isNumber ? this.fetchQuotationQuote(id) : this.fetchQuoteFromConfigit(id)).pipe(
            tap((q) => {
                this.aceLineAdapter = new ConfigitAceQuoteLineAdapter();
                this.modelLineAdapter = new ConfigitModelQuoteLineAdapter();
                this.quoteValidation.check(q);
            })
        );
    }

    private fetchQuoteFromConfigit(id: string): Observable<Quote> {
        return this.httpService.get(`${environment.http.baseUrl}configuration/quotations/${id}?revision=0`).pipe(
            map(({ quote, properties }: { quote: ConfigitQuote; properties: ConfigitQuoteAdditionalProperties }) => ({
                configitQuote: quote,
                isLegacy: true,
                isSaved: true,
                isNew: true,
                ...this.getConfigitQuoteMetaProperties(quote, properties.ownerUserName),
            })),
            tap((result) => this.quote$.next(result))
        );
    }

    private fetchQuotationQuote(id: string): Observable<Quote> {
        return this.httpService
            .get<QuotationQuote>(`${environment.http.quotation}quotations/quote-assist/quotes/${id}`)
            .pipe(
                map((quotationQuote) => ({
                    quotationQuote,
                    isLegacy: false,
                    isSaved: true,
                    isNew: true,
                    ...this.getQuotationQuoteMetaProperties(quotationQuote),
                })),
                tap((result) => this.quote$.next(result))
            );
    }

    private performQuoteUpdateRequest(quote: Quote): Observable<{ version: string }> {
        return quote.isLegacy
            ? this.httpService.put<{ version: string }>(
                  `${environment.http.baseUrl}configuration/quotations/${quote.id}?revision=0`,
                  quote.configitQuote
              )
            : this.httpService.put<{ version: string }>(
                  `${environment.http.quotation}quotations/quote-assist/quotes/${quote.id}`,
                  quote.quotationQuote
              );
    }

    private getConfigitQuoteMetaProperties(quote: ConfigitQuote, type?: string): QuoteMetaProperties {
        const schemes = {
            primary: this.extractQuoteProperty(quote, 'primarySchemes'),
            secondary: this.extractQuoteProperty(quote, 'secondarySchemes'),
            ems: this.extractQuoteProperty(quote, 'emsScheme'),
        };
        const noPriceAdvantage = this.extractQuoteProperty(quote, 'noPriceAdvantage');
        return {
            id: quote.documentId,
            isLegacy: true,
            ...(type && { type }),
            salesOrg: quote.salesArea.salesOrganization,
            customerPurchaseNumber: quote.title,
            quickRef: quote.quickRef,
            status: <QuoteStatus>quote.status.toUpperCase(),
            currency: quote.currency,
            offerNumber: quote.salesDocumentNumber,
            customerNumber: <string>this.extractQuoteProperty(quote, 'companyId'),
            energyLabelHash: this.extractQuoteProperty(quote, 'energyLabelHash'),
            schemes,
            externalApp: this.extractQuoteProperty(quote, 'externalApp'),
            printType: PrintType.toLong(<PrintTypeShort>this.extractQuoteProperty(quote, 'printType')),
            salesforceOpportunityId: this.extractQuoteProperty(quote, 'salesforceId'),
            salesforceRequestId: this.extractQuoteProperty(quote, 'quotationRequestId'),
            salesforcePlanningId: this.extractQuoteProperty(quote, 'planningId'),
            planningType: <PlanningType>this.extractQuoteProperty(quote, 'planningType'),
            sampleQuoteId: this.extractQuoteProperty(quote, 'sampleQuoteId'),
            noPriceAdvantage: typeof noPriceAdvantage === 'string' ? noPriceAdvantage === 'true' : undefined,
            submissionNumber: this.extractQuoteProperty(quote, 'projectIdSF'),
        };
    }

    private getQuotationQuoteMetaProperties({
        id,
        type,
        offer,
        quickRef,
        offerNumber,
    }: QuotationQuote): QuoteMetaProperties {
        const schemes = {
            primary: offer.extraData.primarySchemes,
            secondary: offer.extraData.secondarySchemes,
            ems: offer.extraData.emsScheme,
        };
        return {
            id: id.toString(),
            isLegacy: false,
            type,
            salesOrg: offer.salesOrg,
            customerPurchaseNumber: offer.customerPurchaseNumber,
            quickRef,
            status: offer.extraData.status,
            currency: offer.currency,
            offerNumber: offerNumber || offer.documentNumber,
            customerNumber: offer.customerNumber || '',
            energyLabelHash: offer.extraData.energyLabelHash,
            schemes,
            externalApp: offer.extraData.externalApp,
            printType: <PrintTypeLong>offer.printOptions.printType,
            salesforceOpportunityId: offer.salesforceOpportunityId,
            salesforceRequestId: offer.salesforceRequestId,
            salesforcePlanningId: offer.salesforcePlanningId,
            planningType: <PlanningType>offer.extraData.planningType,
            noPriceAdvantage: offer.extraData.noPriceAdvantage || false,
            sampleQuoteId: offer.template,
            submissionNumber: offer.submissionNumber,
        };
    }

    private extractQuoteProperty(quote: ConfigitQuote, propertyKey: string): string | undefined {
        return quote.properties.find((element) => element.key === propertyKey)?.value?.replace(/"/g, '');
    }

    public updateAceAssignments(assignments: ConfigitAceAssignment[], material: string) {
        const quote = this.getCurrentQuote();
        if (quote) {
            const line = this.aceLineAdapter.getLineByMaterial(quote, material);
            this.aceLineAdapter.setLineAssignments(userAssignments(assignments), line);
            this.updateQuote(quote);
        }
    }

    public updateModelAssignments(assignments: QuoteAssignment[], material: string) {
        const quote = this.getCurrentQuote();
        if (quote) {
            const filteredAssignments = assignments.filter(
                (assignment) =>
                    assignment.isUserAssignment === true ||
                    assignment.isDefault === true ||
                    assignment.variableName === environment.quest.modelSelectionVariable
            );
            const line = this.modelLineAdapter.getLineByMaterial(quote, material);
            this.modelLineAdapter.setLineAssignments(filteredAssignments, line);
            this.updateQuote(quote);
        }
    }

    public setModelLineAssignments(assignments: QuoteAssignment[], line: ConfigitModelQuoteLine) {
        this.modelLineAdapter.setLineAssignments(assignments, line);
    }

    public updateQuoteTitle(value: string) {
        return this.updateQuoteProperty(
            (quote) => this.updateIfChanged(quote.title, value, () => (quote.title = value)),
            (quote) =>
                this.updateIfChanged(
                    quote.offer.customerPurchaseNumber,
                    value,
                    () => (quote.offer.customerPurchaseNumber = value)
                )
        );
    }

    public updateCurrency(currency: string, quote: Quote) {
        if (currency && quote.currency !== currency) {
            this.updateQuoteProperty(
                (q) => this.updateIfChanged(q.currency, currency, () => (q.currency = currency)),
                (q) => this.updateIfChanged(q.offer.currency, currency, () => (q.offer.currency = currency))
            );
            this.updateQuote(quote);
        }
    }

    public updateSchemes(schemes: Scheme) {
        if (Array.isArray(schemes?.primary)) {
            this.updatePrimarySchemes(schemes.primary.join(';'));
        }
        if (Array.isArray(schemes?.secondary)) {
            this.updateSecondarySchemes(schemes.secondary.join(';'));
        }
        if (Array.isArray(schemes?.ems)) {
            this.updateEmsScheme(schemes.ems.join(';'));
        }
    }

    private updatePrimarySchemes(value: string) {
        return this.updateQuoteProperty(
            (quote) => this.updateConfigitQuoteProperty('primarySchemes', value, quote),
            (quote) =>
                this.updateIfChanged(
                    quote.offer.extraData.primarySchemes,
                    value,
                    () => (quote.offer.extraData.primarySchemes = value)
                )
        );
    }

    private updateSecondarySchemes(value: string) {
        return this.updateQuoteProperty(
            (quote) => this.updateConfigitQuoteProperty('secondarySchemes', value, quote),
            (quote) =>
                this.updateIfChanged(
                    quote.offer.extraData.secondarySchemes,
                    value,
                    () => (quote.offer.extraData.secondarySchemes = value)
                )
        );
    }

    private updateEmsScheme(value: string) {
        return this.updateQuoteProperty(
            (quote) => this.updateConfigitQuoteProperty('emsScheme', value, quote),
            (quote) =>
                this.updateIfChanged(
                    quote.offer.extraData.emsScheme,
                    value,
                    () => (quote.offer.extraData.emsScheme = value)
                )
        );
    }

    public updateEnergyLabelHash(value: string) {
        return this.updateQuoteProperty(
            (quote) => this.updateConfigitQuoteProperty('energyLabelHash', value, quote),
            (quote) =>
                this.updateIfChanged(
                    quote.offer.extraData.energyLabelHash,
                    value,
                    () => (quote.offer.extraData.energyLabelHash = value)
                )
        );
    }

    public updateSalesforceOpportunityId(value: string) {
        return this.updateQuoteProperty(
            (quote) => this.updateConfigitQuoteProperty('salesforceId', value, quote),
            (quote) =>
                this.updateIfChanged(
                    quote.offer.salesforceOpportunityId,
                    value,
                    () => (quote.offer.salesforceOpportunityId = value)
                )
        );
    }

    public updateSampleQuoteId(value: string) {
        return this.updateQuoteProperty(
            (quote) => this.updateConfigitQuoteProperty('sampleQuoteId', value, quote),
            (quote) => this.updateIfChanged(quote.offer.template, value, () => (quote.offer.template = value))
        );
    }

    public updatePrintType(value: PrintTypeLong) {
        return this.updateQuoteProperty(
            (quote) => this.updateConfigitQuoteProperty('printType', value, quote),
            (quote) =>
                this.updateIfChanged(
                    quote.offer.printOptions.printType,
                    value,
                    () => (quote.offer.printOptions.printType = value)
                )
        );
    }

    public updateNoPriceAdvantage(value: boolean) {
        return this.updateQuoteProperty(
            (quote) => this.updateConfigitQuoteProperty('noPriceAdvantage', value ? 'true' : 'false', quote),
            (quote) =>
                this.updateIfChanged(
                    quote.offer.extraData.noPriceAdvantage,
                    value,
                    () => (quote.offer.extraData.noPriceAdvantage = value)
                )
        );
    }

    public updateCustomerNumber(value: string) {
        return this.updateQuoteProperty(
            (quote) => this.updateConfigitQuoteProperty('companyId', value, quote),
            (quote) =>
                this.updateIfChanged(quote.offer.customerNumber, value, () => (quote.offer.customerNumber = value))
        );
    }

    private updateQuoteProperty(
        configitUpdateFunc: (quote: ConfigitQuote) => boolean,
        quotationUpdateFunc: (quote: QuotationQuote) => boolean
    ) {
        const data = this.quote$.getValue();
        if (!data) {
            return of(undefined);
        }
        const changed = data.isLegacy
            ? configitUpdateFunc(data.configitQuote)
            : quotationUpdateFunc(data.quotationQuote);
        if (changed) {
            return this.updateQuote(data);
        }
        return of(undefined);
    }

    public updateConfigitQuoteProperty(propertyKey: string, newValue: string, quote: ConfigitQuote): boolean {
        const newSaveValue = typeof newValue === 'string' ? `"${newValue}"` : `${newValue}`;
        const properties = quote.properties || [];
        return this.setQuoteProperty(propertyKey, newSaveValue, properties);
    }

    private updateIfChanged<T>(currentValue: T, newValue: T, setter: () => void): boolean {
        if (currentValue !== newValue) {
            setter();
            return true;
        }
        return false;
    }

    private setQuoteProperty(key: string, value: string, properties: QuoteProperty[]) {
        const current = properties.find((p) => p.key === key);
        if (!current) {
            properties.push({ key, value });
            return true;
        }
        if (current.value !== value) {
            current.value = value;
            return true;
        }
        return false;
    }

    public createAdditionalLine(
        material: string,
        type: LineType,
        engine: ConfigitEngine,
        assignments: (QuoteAssignment | ConfigitAceAssignment)[] = []
    ) {
        const quote = this.getCurrentQuote();
        if (engine === ConfigitEngine.ace) {
            this.aceLineAdapter.createConfigurationLine(material, type, <ConfigitAceAssignment[]>assignments, quote);
        } else {
            this.modelLineAdapter.createConfigurationLine(material, type, <QuoteAssignment[]>assignments, quote);
        }

        if (!quote) {
            return of(undefined);
        }

        this.updateQuote(quote);
        return this.getQuoteSaveObservable();
    }

    public getPrimaryLine(quote: Quote): ConfigitModelQuoteLine {
        return <ConfigitModelQuoteLine>this.getLineByType(quote, LineType.primary);
    }

    public getSecondaryLine(quote: Quote): QuotationQuoteLine {
        return this.getLineByType(quote, LineType.secondary);
    }

    public getTertiaryLine(quote: Quote): QuotationQuoteLine {
        return this.getLineByType(quote, LineType.tertiary);
    }

    public getLineByType(quote: Quote, type: LineType): QuotationQuoteLine {
        return quote.quotationQuote.offer.extraData.lines.find((l) => l.type === type);
    }

    public getExternalProductLines(quote: Quote) {
        return quote.quotationQuote.offer.items.map((i) => ({ material: i.material, quantity: i.quantity }));
    }

    public hasConfiguration(quote: Quote = this.getCurrentQuote()): boolean {
        return quote && !!this.getPrimaryLine(quote);
    }

    public hasConfigitModel(model: string, quote: Quote = this.getCurrentQuote()): boolean {
        const lines = [LineType.primary, LineType.secondary, LineType.tertiary]
            .map((lt) => this.getLineByType(quote, lt))
            .filter(Boolean);
        const aceModel = getAceForConfigitModel(model);
        return lines.some((line) => [model, aceModel].filter(Boolean).includes(line.material));
    }

    public hasSampleQuote(quote: Quote = this.getCurrentQuote()) {
        return !!quote.sampleQuoteId;
    }

    public getSubLines(quote: ConfigitQuote, ...materials: string[]): QuoteLine[] {
        const lines = quote.lines.filter((l) => l.isSubBomItem);
        return materials?.length ? lines.filter((l) => materials.includes(l.variantCode)) : lines;
    }

    private updateQuote(quote: Quote) {
        const data = this.quote$.getValue();
        if (data) {
            const { configitQuote, quotationQuote } = quote;
            this.quote$.next({
                ...data,
                isSaved: false,
                isNew: false,
                ...(quote.isLegacy
                    ? { configitQuote, ...this.getConfigitQuoteMetaProperties(configitQuote) }
                    : { quotationQuote, ...this.getQuotationQuoteMetaProperties(quotationQuote) }),
            });
            return this.getQuoteSaveObservable();
        }
        return of(undefined);
    }

    public getQuoteSaveObservable() {
        return this.quote$.pipe(
            filter((q) => q?.isSaved),
            take(1),
            mapTo(undefined)
        );
    }

    public reset() {
        this.quote$.next(undefined);
    }
}

interface QuoteMetaProperties {
    id: string;
    isLegacy: boolean;
    type: string;
    salesOrg: string;
    customerPurchaseNumber: string;
    quickRef: string;
    status: QuoteStatus;
    offerNumber: string;
    currency: string;
    customerNumber: string;
    externalApp?: string;
    printType?: PrintTypeLong;
    schemes: { primary: string; secondary: string; ems: string };
    energyLabelHash: string;
    salesforceOpportunityId: string;
    salesforceRequestId: string;
    salesforcePlanningId: string;
    planningType: PlanningType;
    sampleQuoteId: string;
    noPriceAdvantage: boolean;
    submissionNumber: string;
}

export interface Quote extends QuoteMetaProperties {
    quotationQuote?: QuotationQuote;
    configitQuote?: ConfigitQuote;
    isSaved: boolean;
    isNew: boolean;
}

interface ConfigitQuoteAdditionalProperties {
    ownerUserName?: string;
}

interface QuoteProperty {
    key: string;
    value: string;
}

export interface QuotationQuote {
    id: number;
    type: string;
    version: string;
    quickRef: string;
    offerNumber: string;
    offer: QuoteOffer;
}

export interface ConfigitQuote {
    language: string;
    documentId: string;
    quickRef: string;
    revisionId: string;
    revision: string;
    title: string;
    status: 'New' | 'Quoted';
    salesDocumentNumber: string;
    lines: QuoteLine[];
    properties: QuoteProperty[];
    version: string;
    currency: string;
    salesArea: {
        salesOrganization: string;
        language: string;
    };
    validTo?: string;
}

export interface QuoteLine {
    id: string;
    lineId: string;
    parentId?: string;
    bomItemId: string;
    isSubBomItem?: boolean;
    variantCode: string;
    quantity: number;
    description: {
        serializedTranslations: SerializedTranslation[];
    };
    properties: QuoteProperty[];
    assignments: QuoteAssignment[];
    language: string;
    materialVariant: {
        language: string;
        code: string;
        image: string;
        assignments: [];
        material: MaterialInfo;
    };
    lineType: number;
    createdDate: string;
    presetSet: boolean;
    modelLineType: number;
    valid: boolean;
}

export interface SerializedTranslation {
    language: string;
    translation: string;
    isDefault: boolean;
}

export interface MaterialInfo {
    language: string;
    properties: QuoteProperty[];
    name: string;
    externalId: string;
    description: {
        serializedTranslations: SerializedTranslation[];
    };
}

export enum LineType {
    primary = 'PrimaryLine',
    secondary = 'SecondaryLine',
    tertiary = 'TertiaryLine',
    product = 'ProductLine',
}
